import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { planLock } from '../../billing/planLock';
import { useTour } from '../../tour/GuidedTour';

// SRS #42 — the guide is a help centre for every plan, not an Enterprise-only
// page. Each card names a *route key*; the workspace the reader is in decides
// the real path (Enterprise shell, Startup/Government shell, or the standard
// app). Plan-locked pages are shown greyed with the upgrade hint from the same
// planLock() the sidebars use, so the guide never advertises a dead link.
export type GuideRoute = 'overview' | 'search' | 'chat' | 'chat-history' | 'search-history' | 'repository' | 'governance'
  | 'comparison' | 'regulations' | 'newsletters' | 'charts' | 'map' | 'team'
  | 'risk' | 'compliance' | 'early-warning' | 'alerts' | 'agents' | 'policy' | 'matters' | 'branding' | 'integrations';
export type Workspace = 'enterprise' | 'startup' | 'government' | 'app';

export function workspaceFor(pathname: string): Workspace {
  if (pathname.startsWith('/enterprise/')) return 'enterprise';
  if (pathname.startsWith('/startup/')) return 'startup';
  if (pathname.startsWith('/government/')) return 'government';
  return 'app';
}

/** Path for a guide card in the given workspace, or null when that workspace has no such page. */
/** Pages that sit at one top-level path in every workspace; the shared menu links them there. */
const SHARED_PATHS: Partial<Record<GuideRoute, string>> = {
  risk: '/risk', compliance: '/compliance', 'early-warning': '/early-warning', alerts: '/alerts',
  agents: '/agents', policy: '/policy', matters: '/matters', branding: '/settings/branding', integrations: '/integrations',
};

export function routeFor(workspace: Workspace, route: GuideRoute): string | null {
  if (SHARED_PATHS[route]) return SHARED_PATHS[route] ?? null;
  if (workspace === 'enterprise') return route === 'overview' ? '/enterprise/dashboard' : `/enterprise/dashboard/${route}`;
  if (workspace === 'startup' || workspace === 'government') {
    const base = `/${workspace}/dashboard`;
    const shell: Partial<Record<GuideRoute, string>> = {
      overview: base, search: `${base}/search`, chat: `${base}/chat`, 'chat-history': `${base}/chat-history`,
      'search-history': `${base}/search-history`, regulations: `${base}/regulations`, newsletters: `${base}/newsletters`,
      charts: `${base}/charts`, map: `${base}/map`,
      repository: '/grc/repository', governance: '/governance', team: '/team', comparison: '/comparison',
    };
    return shell[route] ?? null;
  }
  const app: Partial<Record<GuideRoute, string>> = {
    overview: '/dashboard', search: '/search', chat: '/app', 'chat-history': '/history', 'search-history': '/search-history',
    repository: '/grc/repository', governance: '/governance', comparison: '/comparison', regulations: '/dashboard/all%20regulations',
    newsletters: '/dashboard/newsletters', charts: '/dashboard/chart', map: '/dashboard', team: '/team',
  };
  return app[route] ?? null;
}

/** Navigation entry (server NAVIGATION_FEATURES key) that gates a route, if any. */
const LOCK_ENTRY: Partial<Record<GuideRoute, string>> = {
  comparison: 'comparison', governance: 'governance', team: 'team',
  risk: 'risk', compliance: 'compliance', 'early-warning': 'early_warning', alerts: 'alerts',
  agents: 'agents', policy: 'policy', matters: 'matters', branding: 'branding',
};

interface GuideItem {
  icon: React.ReactNode;
  label: string;
  route: GuideRoute;
  description: string;
  howToUse: string[];
  feature?: string;
  keywords?: string[];
}

interface GuideSection {
  heading: string;
  color: string;
  bg: string;
  headerIcon: React.ReactNode;
  items: GuideItem[];
}

const Icon = {
  Overview: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Search: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/>
    </svg>
  ),
  Chat: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  History: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <circle cx="12" cy="12" r="9"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Bookmark: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Folder: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Compare: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M4 4h6v16H4z"/><path d="M14 4h6v16h-6z"/>
    </svg>
  ),
  Book: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Chart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  Map: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
      <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
    </svg>
  ),
  Team: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
};

const GUIDE_TIPS = [
  {
    title: 'Browse by section',
    text: 'Use the Menu, GRC, Insights, and Admin tiles to jump straight to the area you need.',
  },
  {
    title: 'Search by task',
    text: 'Type words like chat, compare, map, documents, or team access to filter the guide instantly.',
  },
  {
    title: 'Open and act',
    text: 'Each card explains what the page does and the quickest way to start using it inside the workspace.',
  },
];

const GUIDE_SECTIONS: GuideSection[] = [
  {
    heading: 'Menu',
    color: 'var(--ds-link)',
    bg: 'var(--ds-info-bg)',
    headerIcon: <Icon.Overview />,
    items: [
      {
        icon: <Icon.Overview />,
        label: 'Overview',
        route: 'overview',
        feature: 'overview',
        description: 'Start here for a quick snapshot of your workspace, company profile, team activity, and the most important items needing attention.',
        howToUse: [
          'Review the summary cards to check member activity and workspace status.',
          'Use the shortcuts and side menu to move into search, chat, or compliance workflows.',
        ],
        keywords: ['home', 'dashboard', 'summary', 'workspace'],
      },
      {
        icon: <Icon.Search />,
        label: 'Search',
        route: 'search',
        feature: 'search',
        description: 'Find regulations, policies, and legal updates by filtering jurisdiction, topic, industry, and date in one place.',
        howToUse: [
          'Enter a keyword or phrase, then narrow the results using the available filters.',
          'Open a result to inspect the full text, source, and related compliance context.',
        ],
        keywords: ['find regulation', 'policy search', 'filters'],
      },
      {
        icon: <Icon.Chat />,
        label: 'AI Chat',
        route: 'chat',
        feature: 'chat',
        description: 'Ask plain-language compliance questions and get AI answers grounded in cited policy or regulatory sources.',
        howToUse: [
          'Ask a specific question such as a country rule, compliance obligation, or summary request.',
          'Use the cited answer to refine your next prompt or continue the conversation for deeper analysis.',
        ],
        keywords: ['assistant', 'ask question', 'ai help'],
      },
      {
        icon: <Icon.History />,
        label: 'Chat History',
        route: 'chat-history',
        feature: 'chat-history',
        description: 'Return to previous AI conversations, keep useful discussions visible, and continue from where you left off.',
        howToUse: [
          'Search or scan earlier threads to find a prior answer or ongoing compliance discussion.',
          'Reopen a saved chat to resume analysis without repeating the original prompt.',
        ],
        keywords: ['past chats', 'saved conversations', 'resume session'],
      },
      {
        icon: <Icon.Bookmark />,
        label: 'Search History',
        route: 'search-history',
        feature: 'search-history',
        description: 'Revisit earlier lookups and bookmarks so your team can quickly return to already-reviewed regulations.',
        howToUse: [
          'Open recent search entries to repeat or refine a previous investigation.',
          'Use bookmarked items as a shortcut back to important regulatory pages.',
        ],
        keywords: ['recent searches', 'bookmarks', 'history'],
      },
    ],
  },
  {
    heading: 'GRC',
    color: 'var(--ds-link)',
    bg: 'var(--ds-info-bg)',
    headerIcon: <Icon.Shield />,
    items: [
      {
        icon: <Icon.Shield />,
        label: 'Risk',
        route: 'risk',
        feature: 'risk',
        description: 'Assess the risk of each AI system and regulation against the obligations that apply to your organisation.',
        howToUse: [
          'Run a risk assessment to see which obligations apply and where exposure is highest.',
          'Review progress as each finding is addressed.',
        ],
        keywords: ['risk', 'assessment', 'exposure'],
      },
      {
        icon: <Icon.Shield />,
        label: 'Compliance',
        route: 'compliance',
        feature: 'compliance',
        description: 'Check your compliance position against the frameworks and jurisdictions you follow.',
        howToUse: [
          'Start a compliance assessment and answer the questions the workflow asks.',
          'Use the findings to plan remediation with the owners named in them.',
        ],
        keywords: ['compliance', 'assessment', 'frameworks'],
      },
      {
        icon: <Icon.Folder />,
        label: 'Asset Repository',
        route: 'repository',
        feature: 'asset-repository',
        description: 'Keep internal compliance files, policies, evidence, and uploaded documents organised in one shared repository.',
        howToUse: [
          'Upload or review documents your team relies on for audits, controls, and compliance evidence.',
          'Use the repository as the central place to locate the latest approved materials.',
        ],
        keywords: ['documents', 'uploads', 'files', 'repository'],
      },
      {
        icon: <Icon.Shield />,
        label: 'Governance',
        route: 'governance',
        feature: 'governance',
        description: 'Track governance workstreams, control alignment, and internal compliance structure relevant to your organisation.',
        howToUse: [
          'Review the governance view to understand frameworks, controls, and gaps being tracked.',
          'Use it when preparing internal reporting or mapping obligations to business processes.',
        ],
        keywords: ['controls', 'frameworks', 'risk', 'governance'],
      },
      {
        icon: <Icon.Compare />,
        label: 'Comparison',
        route: 'comparison',
        feature: 'comparison',
        description: 'Compare laws or requirements across jurisdictions and topics to quickly spot overlaps, differences, and missing obligations.',
        howToUse: [
          'Select the jurisdictions or topics you want to compare side by side.',
          'Use the output to identify gaps before policy updates or legal reviews.',
        ],
        keywords: ['side by side', 'compare countries', 'gap analysis'],
      },
    ],
  },
  {
    heading: 'Insights',
    color: 'var(--ds-success-text)',
    bg: 'var(--ds-success-bg)',
    headerIcon: <Icon.Chart />,
    items: [
      {
        icon: <Icon.Chart />,
        label: 'Early Warning',
        route: 'early-warning',
        feature: 'early_warning',
        description: 'Signals that show which regulations are moving toward enforcement or changing in a way that matters to you.',
        howToUse: [
          'Review the signals and open the regulation behind each one.',
          'Use them to decide what to brief your team on this week.',
        ],
        keywords: ['early warning', 'signals', 'enforcement'],
      },
      {
        icon: <Icon.Mail />,
        label: 'Alerts',
        route: 'alerts',
        feature: 'alerts',
        description: 'Your custom alerts: the topics, jurisdictions and events you asked to be told about.',
        howToUse: [
          'Create an alert for a jurisdiction, topic or regulation you follow.',
          'Check the alert history to see what has already been sent.',
        ],
        keywords: ['alerts', 'notifications', 'watch'],
      },
      {
        icon: <Icon.Book />,
        label: 'Regulations',
        route: 'regulations',
        feature: 'regulations',
        description: 'Browse the full regulation library, review amendments, and monitor rules relevant to your jurisdictions and industry.',
        howToUse: [
          'Filter by country, date, or topic to narrow the library to the rules that matter most.',
          'Open a regulation detail page to review the summary, source, and compliance context.',
        ],
        keywords: ['law library', 'regulatory list', 'rules'],
      },
      {
        icon: <Icon.Mail />,
        label: 'Newsletters',
        route: 'newsletters',
        feature: 'newsletters',
        description: 'Read curated update briefings that summarise important policy and regulatory developments for your team.',
        howToUse: [
          'Scan the latest updates by topic or region to stay current without reading every source manually.',
          'Use newsletter summaries as a quick briefing before meetings or stakeholder updates.',
        ],
        keywords: ['updates', 'briefings', 'digest'],
      },
      {
        icon: <Icon.Chart />,
        label: 'Charts & Stats',
        route: 'charts',
        feature: 'charts',
        description: 'Visualise activity trends, jurisdiction volume, and changes over time so teams can track where regulation is increasing.',
        howToUse: [
          'Switch time ranges or filters to understand where activity is rising or slowing down.',
          'Use the charts to support reporting, internal reviews, or executive summaries.',
        ],
        keywords: ['analytics', 'statistics', 'trend chart'],
      },
      {
        icon: <Icon.Map />,
        label: 'Global Map',
        route: 'map',
        feature: 'map',
        description: 'See regulations geographically across regions and countries to understand where your organisation may face new exposure.',
        howToUse: [
          'Click through the map to inspect activity by country or region.',
          'Use it for quick visual checks before market expansion or cross-border compliance work.',
        ],
        keywords: ['world map', 'regions', 'countries'],
      },
    ],
  },
  {
    heading: 'Workspace',
    color: 'var(--ds-brand-blue)',
    bg: '#eef4ff',
    headerIcon: <Icon.Folder />,
    items: [
      {
        icon: <Icon.Folder />,
        label: 'Matters',
        route: 'matters',
        feature: 'matters',
        description: 'Client matters and advisory briefs: attach regulations to a matter and draft a brief with cited sources.',
        howToUse: [
          'Create a matter for a client or project and attach the regulations that apply.',
          'Draft a brief, then have a reviewer sign it off before it is shared.',
        ],
        keywords: ['matters', 'briefs', 'clients'],
      },
      {
        icon: <Icon.Book />,
        label: 'Policy Generator',
        route: 'policy',
        feature: 'policy',
        description: 'Draft policy sections grounded in the regulations you track, with the sources shown beside each one.',
        howToUse: [
          'Choose the jurisdictions and the policy you need.',
          'Check every cited source before you adopt a section.',
        ],
        keywords: ['policy', 'generator', 'draft'],
      },
      {
        icon: <Icon.Overview />,
        label: 'Agents',
        route: 'agents',
        feature: 'agents',
        description: 'Standing briefs that watch a scope you define and report back when something moves.',
        howToUse: [
          'Define the scope and the brief the agent should watch.',
          'Read each run\'s briefing and the sources it cites.',
        ],
        keywords: ['agents', 'monitor', 'briefing'],
      },
      {
        icon: <Icon.Shield />,
        label: 'Branding',
        route: 'branding',
        feature: 'branding',
        description: 'Your logo, colours and watermark, used in the app header and on every report.',
        howToUse: [
          'Set your brand name and upload a logo.',
          'Turn the \'Powered by Policyora\' line on or off.',
        ],
        keywords: ['branding', 'logo', 'white label'],
      },
      {
        icon: <Icon.Mail />,
        label: 'Integrations',
        route: 'integrations',
        feature: 'integrations',
        description: 'Connect the tools your team already uses.',
        howToUse: [
          'Open Integrations and connect the service you need.',
          'Check the connection status before you rely on it.',
        ],
        keywords: ['integrations', 'connect', 'api'],
      },
    ],
  },
  {
    heading: 'Admin',
    color: 'var(--ds-link)',
    bg: 'var(--ds-info-bg)',
    headerIcon: <Icon.Team />,
    items: [
      {
        icon: <Icon.Team />,
        label: 'Team Access',
        route: 'team',
        feature: 'team',
        description: 'Manage who can enter the workspace, what each person can access, and how seats are being used across the enterprise team.',
        howToUse: [
          'Invite new members and assign only the pages or features they need.',
          'Review active and inactive users to keep access clean and current.',
        ],
        keywords: ['members', 'permissions', 'roles', 'seats'],
      },
    ],
  },
];

export default function EnterpriseUserGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const billing = useBilling();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const workspace = workspaceFor(location.pathname);
  const tour = useTour();

  const allowedFeatures: string[] = user?.enterprise_allowed_features ?? [];
  const isEnterpriseMember = Boolean(user?.is_enterprise_member);
  const normalizedSearch = search.trim().toLowerCase();

  const canAccess = (feature?: string) => {
    if (!isEnterpriseMember) return true;
    if (!feature) return true;
    return allowedFeatures.map(f => f.toLowerCase()).includes(feature.toLowerCase());
  };

  const filteredSections = useMemo(() => {
    return GUIDE_SECTIONS.map(section => ({
      ...section,
      items: section.items.filter(item =>
        canAccess(item.feature) &&
        (!normalizedSearch ||
          item.label.toLowerCase().includes(normalizedSearch) ||
          item.description.toLowerCase().includes(normalizedSearch) ||
          item.howToUse.some(step => step.toLowerCase().includes(normalizedSearch)) ||
          (item.keywords ?? []).some(keyword => keyword.toLowerCase().includes(normalizedSearch)))
      ),
    })).filter(s => s.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedSearch, isEnterpriseMember, allowedFeatures.join(',')]);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      (el as HTMLElement).scrollIntoView();
    }
  };

  return (
    <div style={{ minHeight: '100%', background: 'var(--ds-surface-2)' }}>
      <div style={{
        background: 'var(--ds-info-bg)',
        padding: '44px 40px 40px',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid var(--ds-border)',
      }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.45,
          backgroundImage: 'var(--ds-border-strong)',
          backgroundSize: '26px 26px',
        }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 980 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--ds-surface)',
            borderRadius: 999,
            border: '1px solid var(--ds-info-bg)',
            padding: '5px 14px',
            marginBottom: 14,
            boxShadow: '0 4px 12px rgba(164, 188, 253, 0.12)',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--ds-text-secondary)" strokeWidth="2" width="14" height="14">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
            </svg>
            <span style={{ color: 'var(--ds-text-secondary)', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em' }}>
              ENTERPRISE WORKSPACE
            </span>
          </div>

          <h1 style={{ color: 'var(--ds-text)', fontSize: 34, fontWeight: 800, margin: '0 0 10px', letterSpacing: '-0.02em' }}>
            User Guide
          </h1>
          <p style={{ color: 'var(--ds-text-secondary)', fontSize: 15, lineHeight: 1.7, margin: '0 0 22px', maxWidth: 760 }}>
            Explore every page in your enterprise dashboard, understand what each area is for, and see the fastest way to use it in your daily workflow.
          </p>

          <div style={{ maxWidth: 560, position: 'relative', marginBottom: 18 }}>
            <svg
              style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-text-faint)' }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="18"
              height="18"
            >
              <circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search a page, task, or keyword..."
              style={{
                width: '100%',
                padding: '14px 18px 14px 46px',
                borderRadius: 40,
                border: '1px solid var(--ds-info-bg)',
                fontSize: 14,
                background: 'var(--ds-surface)',
                color: 'var(--ds-text)',
                boxShadow: '0 10px 24px rgba(16, 24, 40, 0.06)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={() => scrollToSection('guide-content')}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'var(--ds-info-bg)',
                border: '1px solid var(--primary-200)',
                borderRadius: 30,
                padding: '8px 18px',
                color: 'var(--ds-info-text)',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Search
            </button>
          </div>

          {/* SRS #39: replay the first-time tour from here */}
          <button
            type="button"
            onClick={() => { navigate(routeFor(workspace, 'overview') || '/dashboard'); setTimeout(tour.start, 300); }}
            style={{ marginBottom: 14, padding: '8px 16px', borderRadius: 999, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ▶ Take the guided tour (Search → Charts → Digest → Chat)
          </button>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            maxWidth: 860,
          }}>
            {GUIDE_TIPS.map((tip) => (
              <div
                key={tip.title}
                style={{
                  background: 'var(--ds-surface)',
                  border: '1px solid var(--ds-border)',
                  borderRadius: 14,
                  padding: '12px 14px',
                  boxShadow: '0 4px 14px rgba(164, 188, 253, 0.08)',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 4 }}>{tip.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--ds-text-muted)', lineHeight: 1.55 }}>{tip.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 0,
        borderBottom: '1px solid var(--ds-border)',
        background: 'var(--ds-info-bg)',
      }}>
        {GUIDE_SECTIONS.filter(s => s.items.some(i => canAccess(i.feature))).map((section, i, arr) => (
          <button
            key={section.heading}
            onClick={() => scrollToSection(`section-${section.heading}`)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              padding: '22px 16px',
              background: 'none',
              border: 'none',
              borderRight: i < arr.length - 1 ? '1px solid var(--ds-border)' : 'none',
              cursor: 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = section.bg)}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: section.bg,
              color: section.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {section.headerIcon}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ds-text)' }}>{section.heading}</div>
            <div style={{ fontSize: 11, color: 'var(--ds-text-faint)' }}>
              {section.items.filter(item => canAccess(item.feature)).length} pages
            </div>
          </button>
        ))}
      </div>

      <div id="guide-content" style={{ padding: '28px 32px 60px' }}>
        <div style={{
          background: 'var(--ds-surface)',
          border: '1px solid var(--ds-border)',
          borderRadius: 16,
          padding: '18px 20px',
          marginBottom: 24,
          boxShadow: '0 8px 20px rgba(16, 24, 40, 0.03)',
        }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 16, color: 'var(--ds-text)' }}>How to use this guide</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ds-text-muted)', lineHeight: 1.7 }}>
            Start with the section tiles above, then open any card below to see what the page is for and the easiest first steps to take inside that page.
          </p>
        </div>

        {filteredSections.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ds-text-faint)' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40" style={{ marginBottom: 12 }}>
              <circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <p style={{ fontSize: 15, margin: 0 }}>No results for "{search}"</p>
          </div>
        )}

        {filteredSections.map(section => (
          <div key={section.heading} id={`section-${section.heading}`} style={{ marginBottom: 36 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: section.bg,
                color: section.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {section.headerIcon}
              </div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ds-text)', margin: 0 }}>{section.heading}</h2>
              <div style={{ flex: 1, height: 1, background: 'var(--ds-border)', marginLeft: 4 }} />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
              gap: 14,
            }}>
              {section.items.map(item => {
                const path = routeFor(workspace, item.route);
                const entry = LOCK_ENTRY[item.route];
                const lock = entry ? planLock(billing, entry) : { locked: false, title: '' };
                const unavailable = !lock.locked && path === null;
                const hint = lock.locked ? lock.title : unavailable ? 'Available in the Enterprise workspace' : '';
                return (
                <button
                  key={item.label}
                  onClick={() => {
                    if (lock.locked) navigate('/pricing', { state: { changeOnly: true } });
                    else if (path) navigate(path);
                  }}
                  aria-label={item.label}
                  title={hint || item.label}
                  aria-disabled={unavailable || undefined}
                  data-guide-state={lock.locked ? 'locked' : unavailable ? 'unavailable' : 'open'}
                  style={{
                    opacity: lock.locked || unavailable ? 0.6 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    textAlign: 'left',
                    background: 'var(--ds-surface)',
                    border: '1px solid var(--ds-border)',
                    borderRadius: 16,
                    padding: '18px 18px 16px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget;
                    el.style.borderColor = section.color;
                    el.style.boxShadow = `0 10px 24px ${section.bg}, 0 1px 4px rgba(0,0,0,0.06)`;
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget;
                    el.style.borderColor = 'var(--ds-border)';
                    el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    el.style.transform = 'none';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: section.bg,
                      color: section.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {item.icon}
                    </div>
                    <svg viewBox="0 0 20 20" fill="none" stroke="var(--ds-border-strong)" strokeWidth="1.5" width="16" height="16">
                      <path d="M5 10h10M10 5l5 5-5 5"/>
                    </svg>
                  </div>

                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ds-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.label}
                      {hint && <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ds-warning-text)', background: 'var(--ds-warning-bg)', border: '1px solid #fedf89', borderRadius: 999, padding: '1px 7px' }}>{lock.locked ? 'Upgrade' : 'Enterprise'}</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--ds-text-muted)', lineHeight: 1.65 }}>
                      {item.description}
                    </div>
                  </div>

                  <div style={{
                    background: 'var(--ds-surface-2)',
                    border: '1px solid #fcfcfd',
                    borderRadius: 12,
                    padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ds-text-secondary)', marginBottom: 6 }}>
                      How to use it
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--ds-text-muted)', fontSize: 12.5, lineHeight: 1.6 }}>
                      {item.howToUse.map(step => (
                        <li key={step} style={{ marginBottom: 4 }}>{step}</li>
                      ))}
                    </ul>
                  </div>

                  <div style={{
                    marginTop: 'auto',
                    paddingTop: 10,
                    borderTop: '1px solid var(--ds-surface-2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    fontWeight: 700,
                    color: section.color,
                  }}>
                    {lock.locked ? 'See plans' : unavailable ? 'Enterprise workspace only' : `Open ${item.label}`}
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <path d="M3 8h10M8 3l5 5-5 5"/>
                    </svg>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
